import { Link } from "react-router-dom";
import { Printer, Download, ArrowLeft, ShieldCheck, Award, FileCheck, CheckCircle2 } from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const WARRANTY = [
  ["Coverage", "Limited warranty against manufacturing defects, delamination & structural failure under rated loads."],
  ["Term", "Multi-year limited warranty (see contract terms for the applicable period)."],
  ["Rated conditions", "Use within published load, temperature & handling specifications."],
  ["Exclusions", "Misuse, overloading beyond rated capacity, unauthorized modification or improper handling."],
  ["Remedy", "Repair or replacement of qualifying units at OneHarvest's discretion."],
  ["Support", "Dedicated account & field-support contact for claims and RMA."],
];

const CERTS = [
  "Material: UV-stabilized HDPE composite, 100% recyclable",
  "Load performance verified to published psi / point-load ratings",
  "Non-porous surface — zero fluid absorption (decontamination-ready)",
  "OSHA-friendly walking/working surface characteristics",
  "SWPPP-compatible ground protection",
  "Manufactured to ISO-aligned quality processes",
];

const SUBMITTAL = [
  ["Technical Data Sheet", "Dimensions, load ratings, material & install specs"],
  ["CAD Drawings", "Panel & connection geometry (on request)"],
  ["FEA Data", "Finite element load-distribution outputs (on request)"],
  ["Material Declaration", "Recyclability & composition statement (on request)"],
];

export default function WarrantyBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm"><ArrowLeft className="h-4 w-4" /> Back to product</Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Warranty &amp; Certifications</span>
            <a href="/big-bad-mats-warranty.pdf" download data-testid="download-warranty" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors"><Download className="h-4 w-4" /> Download PDF</a>
            <button onClick={() => window.print()} data-testid="print-warranty" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors"><Printer className="h-4 w-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="warranty-sheet" className="bg-white shadow-2xl print:shadow-none w-[210mm] h-[297mm] p-[13mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
              </div>
              <div className="inline-flex items-center border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.2em]">Warranty &amp; Certifications</div>
              <h1 className="mt-2 font-display font-black tracking-tight text-ink leading-none text-[28px]">Big Bad Mats</h1>
              <p className="mt-1 font-display font-bold text-charcoal text-[12px]">Warranty Statement · Certifications · Submittal Package</p>
            </div>
            <ShieldCheck className="h-14 w-14 text-flame" />
          </header>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2"><Award className="h-4 w-4 text-flame" /><h2 className="font-display font-bold text-ink text-[13px] uppercase tracking-wider">Limited Warranty</h2></div>
            <div className="border border-border">
              {WARRANTY.map(([k, v], i) => (
                <div key={k} className={`grid grid-cols-[0.7fr_2fr] ${i % 2 ? "bg-muted/40" : "bg-white"}`}>
                  <dt className="px-3 py-2 font-display font-semibold text-ink text-[10.5px] border-r border-border">{k}</dt>
                  <dd className="px-3 py-2 text-charcoal text-[10.5px]">{v}</dd>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-flame" /><h2 className="font-display font-bold text-ink text-[13px] uppercase tracking-wider">Certifications &amp; Conformance</h2></div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
              {CERTS.map((c) => (
                <li key={c} className="flex items-start gap-2 text-charcoal text-[10.5px] leading-snug"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />{c}</li>
              ))}
            </ul>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2"><FileCheck className="h-4 w-4 text-flame" /><h2 className="font-display font-bold text-ink text-[13px] uppercase tracking-wider">Submittal Package</h2></div>
            <div className="grid grid-cols-2 gap-3">
              {SUBMITTAL.map(([t, d]) => (
                <div key={t} className="border border-border p-3">
                  <div className="font-display font-bold text-ink text-[11px]">{t}</div>
                  <div className="text-muted-foreground text-[9.5px] mt-0.5">{d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-auto pt-4">
            <div className="bg-ink text-white flex items-center justify-between gap-4 px-5 py-3">
              <div>
                <div className="font-display font-extrabold text-[14px]">Request the full submittal &amp; warranty documents.</div>
                <div className="text-white/60 text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
              </div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden">Request Documents</Link>
            </div>
            <p className="mt-2 text-[8px] text-muted-foreground text-center">Warranty terms, certifications &amp; conformance statements are representative — governed by the executed sales contract &amp; current certified documentation. © {new Date().getFullYear()} {COMPANY.legal}.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
